import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Minus, Plus, ShieldCheck } from "lucide-react";
import { Header } from "@/components/site/Header";
import { LiveTracker } from "@/components/site/LiveTracker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/context/CartContext";
import { api, errMsg, img, inr } from "@/lib/api";

const STEPS = ["Bag", "Delivery", "Payment", "Confirmed"];
const field = "bg-[#14110E] border-[#D4AF37]/20 h-11";
const gold = "btn-glow w-full rounded-full bg-[#D4AF37] py-4 font-semibold uppercase tracking-[0.2em] text-sm text-[#0B0907] hover:bg-[#F3E5AB] disabled:opacity-40";

const loadRzp = () =>
  new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });

function Stepper({ step }) {
  return (
    <ol className="flex items-center justify-center md:justify-start gap-2 sm:gap-4 mb-10" data-testid="checkout-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="flex items-center gap-2 sm:gap-4">
          <span className={`h-7 w-7 rounded-full grid place-items-center text-xs border ${i <= step ? "bg-[#D4AF37] text-[#0B0907] border-[#D4AF37]" : "border-[#D4AF37]/25 text-[#6E6558]"}`}>{i + 1}</span>
          <span className={`hidden sm:inline text-xs uppercase tracking-[0.2em] ${i <= step ? "text-[#F3E5AB]" : "text-[#6E6558]"}`}>{s}</span>
          {i < STEPS.length - 1 && <span className="w-6 sm:w-10 h-px bg-[#D4AF37]/25" />}
        </li>
      ))}
    </ol>
  );
}

function BagStep({ next }) {
  const { items, setQty } = useCart();
  if (!items.length) return <p className="font-serif-it text-2xl text-[#A89F91]" data-testid="checkout-empty">Your bag is empty. <Link to="/store" className="text-[#F3E5AB] underline">Explore the store</Link></p>;
  return (
    <div className="space-y-4" data-testid="step-bag">
      {items.map((i) => (
        <div key={i.id} className="flex items-center gap-4 border-b border-[#D4AF37]/10 pb-4">
          <img src={img(i.image_url)} alt="" className="h-20 w-16 object-cover rounded" />
          <div className="flex-1 min-w-0"><p className="font-display truncate">{i.name}</p><p className="font-serif-it text-[#A89F91]">{i.origin}</p></div>
          <div className="flex items-center gap-2">
            <button onClick={() => setQty(i.id, i.qty - 1)} className="p-1 border border-[#D4AF37]/30 rounded-full" data-testid={`bag-dec-${i.id}`}><Minus className="h-3 w-3" /></button>
            <span className="w-5 text-center text-sm">{i.qty}</span>
            <button onClick={() => setQty(i.id, i.qty + 1)} className="p-1 border border-[#D4AF37]/30 rounded-full" data-testid={`bag-inc-${i.id}`}><Plus className="h-3 w-3" /></button>
          </div>
        </div>
      ))}
      <button onClick={next} className={gold} data-testid="bag-continue-button">Continue to delivery</button>
    </div>
  );
}

function DeliveryStep({ f, setF, next, back }) {
  const ch = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); next(); };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="checkout-form">
      <Input required minLength={2} placeholder="Full name" value={f.name} onChange={ch("name")} className={field} data-testid="checkout-name" />
      <Input required minLength={6} placeholder="Phone number" value={f.phone} onChange={ch("phone")} className={field} data-testid="checkout-phone" />
      <Input type="email" placeholder="Email (optional)" value={f.email} onChange={ch("email")} className={field} data-testid="checkout-email" />
      <Textarea required minLength={5} placeholder="Delivery address" value={f.address} onChange={ch("address")} className="bg-[#14110E] border-[#D4AF37]/20 min-h-28" data-testid="checkout-address" />
      <button className={gold} data-testid="delivery-continue-button">Continue to payment</button>
      <button type="button" onClick={back} className="w-full text-xs uppercase tracking-[0.2em] text-[#6E6558] hover:text-[#A89F91]" data-testid="delivery-back-button">Back to bag</button>
    </form>
  );
}

function PaymentStep({ f, done, back }) {
  const { items, total, clear } = useCart();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then((r) => setCfg(r.data));
  }, []);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...f, items: items.map((i) => ({ product_id: i.id, qty: i.qty })) });
      if (data.payment !== "razorpay") {
        clear();
        return done(data);
      }
      if (!(await loadRzp())) throw new Error("Could not load Razorpay");
      new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: "INR", order_id: data.razorpay_order_id,
        name: "Ancient Origin", description: data.order_number,
        prefill: { name: f.name, email: f.email, contact: f.phone },
        theme: { color: "#D4AF37" },
        handler: async (r) => {
          try {
            const v = await api.post("/payments/verify", { order_id: data.order_id, ...r });
            clear();
            done(v.data);
          } catch (e) {
            toast.error(errMsg(e));
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      }).open();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="step-payment">
      <div className="rounded-sm border border-[#D4AF37]/20 bg-[#14110E] p-5 flex gap-4">
        <CreditCard className="h-6 w-6 text-[#D4AF37] shrink-0" />
        {cfg?.enabled ? (
          <div><p className="font-display">Pay securely with Razorpay</p><p className="text-sm text-[#A89F91] mt-1">UPI, cards, net banking and wallets.</p></div>
        ) : (
          <div data-testid="payment-pending-setup"><p className="font-display">Pay on confirmation</p><p className="text-sm text-[#A89F91] mt-1">Online payment (Razorpay) switches on once the store adds its keys. For now, our team will call you to collect payment.</p></div>
        )}
      </div>
      <p className="flex items-center gap-2 text-xs text-[#6E6558]"><ShieldCheck className="h-4 w-4" /> Delivering to {f.name}, {f.phone}</p>
      <button disabled={busy || !cfg} onClick={pay} className={gold} data-testid="pay-button">
        {busy ? "Processing…" : cfg?.enabled ? `Pay ${inr(total)}` : `Place order · ${inr(total)}`}
      </button>
      <button onClick={back} className="w-full text-xs uppercase tracking-[0.2em] text-[#6E6558] hover:text-[#A89F91]" data-testid="payment-back-button">Back to delivery</button>
    </div>
  );
}

function Summary() {
  const { items, total } = useCart();
  return (
    <aside className="md:col-span-2 border border-[#D4AF37]/15 bg-[#14110E] rounded-sm p-6 h-fit" data-testid="checkout-summary">
      <p className="font-display tracking-[0.15em] text-[#F3E5AB] text-sm">SUMMARY</p>
      <div className="mt-5 space-y-3">
        {items.map((i) => (
          <div key={i.id} className="flex justify-between gap-3 text-sm"><span className="truncate">{i.name} × {i.qty}</span><span className="text-[#F3E5AB]">{inr(i.price * i.qty)}</span></div>
        ))}
      </div>
      <div className="zari-divider my-6" />
      <div className="flex justify-between font-display"><span>Total</span><span className="text-[#F3E5AB]" data-testid="checkout-total">{inr(total)}</span></div>
    </aside>
  );
}

export default function Checkout() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ name: "", phone: "", email: "", address: "" });
  const [done, setDone] = useState(null);
  const finish = (d) => { setDone(d); setStep(3); };

  return (
    <div className="min-h-screen" data-testid="checkout-page">
      <Header />
      <div className="mx-auto max-w-5xl px-6 pt-32 pb-20">
        <Stepper step={step} />
        {done ? (
          <div className="text-center fade-up" data-testid="order-success">
            <p className="text-[11px] uppercase tracking-[0.5em] text-[#D4AF37]">Order placed</p>
            <h1 className="font-display text-4xl sm:text-6xl mt-4">Your story begins.</h1>
            <p className="font-serif-it text-2xl text-[#A89F91] mt-4">Order number <span className="text-[#F3E5AB] not-italic font-display" data-testid="order-number">{done.order_number}</span> · {inr(done.total)}</p>
            <LiveTracker orderNumber={done.order_number} />
            <Link to="/store" className="btn-glow inline-block mt-10 rounded-full border border-[#D4AF37]/50 px-8 py-3 text-xs uppercase tracking-[0.25em] text-[#F3E5AB] hover:bg-[#D4AF37] hover:text-[#0B0907]" data-testid="continue-shopping">Continue shopping</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-5 gap-12">
            <div className="md:col-span-3">
              <h1 className="font-display text-3xl sm:text-4xl mb-6">{["Your bag", "Delivery details", "Payment"][step]}</h1>
              {step === 0 && <BagStep next={() => setStep(1)} />}
              {step === 1 && <DeliveryStep f={f} setF={setF} next={() => setStep(2)} back={() => setStep(0)} />}
              {step === 2 && <PaymentStep f={f} done={finish} back={() => setStep(1)} />}
            </div>
            <Summary />
          </div>
        )}
      </div>
    </div>
  );
}
